// src/pages/Delivery/CancelledOrdersList.jsx
import React, { useState, useEffect, useCallback } from "react";
import Header from "../../../components/layout/Header/Header";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import DirhamSymbol from "../../../Assets/aed-symbol.png";
import TableScrollSync from "../../../components/common/TableScrollSync";

import "./CancelledOrdersList.css";
import axios from "axios";
import { useAppSettings } from "../../../context/AppSettingsContext";
import { usePaginatedData } from "../../../hooks/usePagination";
import Pagination from "../../../components/common/Pagination";

const CancelledOrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("Cancelled Orders"); // fixed typo
  const [user, setUser] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_IP;

  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        window.location.href = "/login";
        return;
      }

      const response = await axios.get(`${backendUrl}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data.user || response.data);
    } catch (error) {
      console.error("Failed to load user", error);
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  }, [backendUrl]);

  const fetchCancellableOrders = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${backendUrl}/api/orders/my-assigned-orders`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    // Two distinct things land here: orders the delivery man rejected
    // (assignmentStatus === "rejected"), and orders that were actually
    // cancelled by admin/salesman/manager before packing started (order.status
    // === "cancelled" — this can happen whether the assignment was merely
    // "assigned" or already "accepted", since cancelOrder doesn't touch
    // assignmentStatus). Accepted-and-still-active orders continue through
    // Accepted/Delivered Orders instead.
    const cancellableOrders = response.data.filter(
      (order) => order.assignmentStatus === "rejected" || order.status === "cancelled"
    );
    setOrders(cancellableOrders);
  } catch (error) {
    console.error('Error fetching cancellable orders:', error);
    alert('Failed to load orders');
  } finally {
    setLoading(false);
  }
}, [backendUrl]);

  useEffect(() => {
    fetchCurrentUser();
    fetchCancellableOrders();
  }, [fetchCurrentUser, fetchCancellableOrders]);

  // Helper function to format date as DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const { entriesPerPage } = useAppSettings();
  const pagination = usePaginatedData(orders, entriesPerPage, "cancelled-orders");

  if (!user) {
    return <div className="cancelled-orders-loading">Loading...</div>;
  }

  return (
    <div className="cancelled-orders-layout">
      <Header
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        user={user}
      />
      <Sidebar
        isOpen={sidebarOpen}
        activeItem={activeItem}
        onSetActiveItem={setActiveItem}
        onClose={() => setSidebarOpen(false)}
        user={user}
      />
      <main
        className={`cancelled-orders-main-content ${sidebarOpen ? "sidebar-open" : ""}`}
      >
        <div className="cancelled-orders-container-wrapper">
          <div className="cancelled-orders-container">
            <h2 className="cancelled-orders-page-title">Cancel Orders</h2>

            {loading ? (
              <div className="cancelled-orders-loading">Loading orders...</div>
            ) : (
              <>
                <TableScrollSync>
                  <div className="cancelled-orders-table-wrapper">
                    <table className="cancelled-orders-data-table">
                      <thead>
                        <tr>
                          <th scope="col">No</th>
                          <th scope="col">Customer</th>
                          <th scope="col">Reason</th>
                          <th scope="col">Product</th>
                          <th scope="col">Ordered Qty</th>
                          <th scope="col">Delivered Qty</th>
                          <th scope="col">Price</th>
                          <th scope="col">Total Amount</th>
                          <th scope="col">Order Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagination.pageData.length > 0 ? (
                          pagination.pageData.map((order, index) => {
                            const items = order.orderItems || [];
                            const orderedQty = items.reduce((s, it) => s + (it.orderedQuantity || 0), 0);
                            const deliveredQty = items.reduce((s, it) => s + (it.deliveredQuantity || 0), 0);
                            const grandTotal = items.reduce((s, it) => s + (it.totalAmount || 0), 0);
                            const productNames = items
                              .map((it) => it.product?.productName || "N/A")
                              .join(", ");
                            const singlePrice = items.length === 1 ? items[0].price : null;
                            return (
                              <tr key={order._id}>
                                <td>{pagination.showingFrom + index}</td>
                                <td>{order.customer?.name || "N/A"}</td>
                                <td>
                                  <span
                                    className={`cancelled-orders-reason-badge ${
                                      order.status === "cancelled" ? "is-cancelled" : "is-rejected"
                                    }`}
                                  >
                                    {order.status === "cancelled" ? "Cancelled by Admin" : "Rejected by you"}
                                  </span>
                                </td>
                                <td>{productNames || "N/A"}</td>
                                <td>{orderedQty}</td>
                                <td>{deliveredQty}</td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    {singlePrice != null ? (
                                      <>
                                        <img
                                          src={DirhamSymbol}
                                          alt="Dirham Symbol"
                                          width={15}
                                          height={15}
                                          style={{ paddingTop: "3px" }}
                                        />
                                        <span>{Number(singlePrice).toFixed(2)}</span>
                                      </>
                                    ) : (
                                      <span>—</span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <img
                                      src={DirhamSymbol}
                                      alt="Dirham Symbol"
                                      width={15}
                                      height={15}
                                      style={{ paddingTop: "3px" }}
                                    />
                                    <span>{Number(grandTotal).toFixed(2)}</span>
                                  </div>
                                </td>
                                <td>{formatDate(order.orderDate)}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="9" className="cancelled-orders-no-data">
                              No orders available for cancellation
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </TableScrollSync>

                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalRecords={pagination.totalRecords}
                  showingFrom={pagination.showingFrom}
                  showingTo={pagination.showingTo}
                  canPrev={pagination.canPrev}
                  canNext={pagination.canNext}
                  onPrev={pagination.goPrev}
                  onNext={pagination.goNext}
                />
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CancelledOrdersList;
